import React, { useEffect, useRef, useState } from 'react';
import './TweetEmbed.css';

let widgetsPromise = null;

function loadTwitterWidgets() {
  if (window.twttr?.widgets) return Promise.resolve(window.twttr);
  if (widgetsPromise) return widgetsPromise;

  widgetsPromise = new Promise((resolve) => {
    const existing = document.querySelector('script[src*="platform.twitter.com/widgets"]');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.src = 'https://platform.twitter.com/widgets.js';
    script.async = true;
    script.charset = 'utf-8';

    let attempts = 0;
    const check = () => {
      if (window.twttr?.widgets) {
        resolve(window.twttr);
      } else if (attempts++ < 50) {
        setTimeout(check, 200);
      } else {
        widgetsPromise = null;
        resolve(null);
      }
    };

    script.onload = check;
    script.onerror = () => { widgetsPromise = null; resolve(null); };
    document.head.appendChild(script);
  });

  return widgetsPromise;
}

// Global cache: once a tweet iframe is created, store the DOM node so it
// survives React unmount/remount cycles caused by ReactMarkdown re-renders.
const tweetNodeCache = new Map();

function TweetEmbed({ tweetId, url }) {
  const containerRef = useRef(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // If we already rendered this tweet before, re-attach the cached DOM node
    const cached = tweetNodeCache.get(tweetId);
    if (cached) {
      container.innerHTML = '';
      container.appendChild(cached);
      setStatus('rendered');
      return;
    }

    let cancelled = false;

    const timeout = setTimeout(() => {
      if (!cancelled && status === 'loading') setStatus('error');
    }, 20000);

    let retries = 0;

    const tryEmbed = async () => {
      const twttr = await loadTwitterWidgets();
      if (cancelled || !containerRef.current) return;

      if (!twttr) { setStatus('error'); return; }

      // Create a detached div for Twitter to render into — this way the
      // rendered iframe lives outside React's VDOM and won't be destroyed.
      const renderTarget = document.createElement('div');

      try {
        const el = await twttr.widgets.createTweet(tweetId, renderTarget, {
          theme: 'dark',
          dnt: true,
          align: 'center'
        });

        if (cancelled) return;

        if (el) {
          // Cache the rendered node and attach it
          tweetNodeCache.set(tweetId, renderTarget);
          if (containerRef.current) {
            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(renderTarget);
          }
          setStatus('rendered');
        } else if (retries++ < 2) {
          setTimeout(() => { if (!cancelled) tryEmbed(); }, 2000);
        } else {
          setStatus('error');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    };

    tryEmbed();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      // Don't clear container — the cached node will be re-attached on remount
    };
  }, [tweetId]);

  if (status === 'error') {
    return (
      <div className="tweet-embed-fallback">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
        <a href={url} target="_blank" rel="noopener noreferrer">
          View post on X
        </a>
      </div>
    );
  }

  return (
    <div className="tweet-embed-wrapper">
      {status === 'loading' && (
        <div className="tweet-embed-skeleton">
          <div className="tweet-skeleton-header">
            <div className="tweet-skeleton-avatar" />
            <div className="tweet-skeleton-name" />
          </div>
          <div className="tweet-skeleton-line" />
          <div className="tweet-skeleton-line short" />
        </div>
      )}
      <div ref={containerRef} />
    </div>
  );
}

export default TweetEmbed;
